// Complete App.js with Enhanced Token Manager for Remember Me functionality
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { setupAutoRefresh, enhancedTokenManager } from './utils/enhancedTokenManager'; // Enhanced token manager
import { DarkModeProvider } from './contexts/DarkModeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ErrorProvider, useError } from './contexts/ErrorContext';
import NetworkErrorPopup from './components/NetworkErrorPopup';

// Import notification service
import { useVaadsNotifications } from './Pages/VaadsMessages/NotificationService';
import { markMessagesAsRead } from './Fetch/fetchVaadsMessages';

// Import subscription service
import { getBuildingSubscription } from './Fetch/fetchSubscription';

// Emails exempt from trial/subscription expiration checks
import { isTrialBypassUser } from './utils/trialBypass';

// Import subscription warning component
import SubscriptionWarning from './Pages/Billing/SubscriptionWarning';

// Layout
import Layout from './Pages/Layout/Layout';

// Landing Page
import LandingPage from './Pages/LandingPage/LandingPage';

// Public pages
import Login from './Pages/Account/Login';
import Register from './Pages/Account/Registry';
import ForgotPassword from './Pages/Account/ForgotPassword';
import SubscriptionPayment from './Pages/Billing/SubscriptionPayment';

// Protected pages
import Dashboard from './Pages/Home/Dashboard';
import AdminDashboard from './Pages/Admin/AdminDashboard';
import UsersManagement from './Pages/Users/UsersManagement';
import ManagersManagement from './Pages/Managers/ManagersManagement';
import ManagementCompany from './Pages/ManagementCompany/ManagementCompany';
import Messages from './Pages/Message/Message';
import VaadsMessages from './Pages/VaadsMessages/VaadsMessages';
import TaskManagement from './Pages/Tasks/TaskManagement';
import Event from './Pages/Events/Event';
import Payment from './Pages/Payments/Payment';
import Expenses from './Pages/Expenses/Expenses';
import ElectricVehicle from './Pages/ElectricVehicle';
import BankStatus from './Pages/BankStatus/BankStatus';
import Notifications from './Pages/Notifications/Notifications';
import Announcement from './Pages/Announcement/Announcement';
import Settings from './Pages/Settings/Settings';
import Profile from './Pages/Profile/Profile';
import Issue from './Pages/Issues/Issue';
import Reports from './Pages/Report/Reports';
import ExtDashboard from './Pages/ExternalPage/ExtDashboard';
import ExternalTaskPage from './Pages/ExternalTaskPage/ExternalTaskPage';
import PersonalInformation from './Pages/PersonalInformation/PersonalInformation';
import AddBuilding from './Pages/Building/AddBuilding';

// Create Notification Context
const NotificationContext = createContext();

// Enhanced subscription monitoring hook with CORRECT logic
const useSubscriptionMonitoring = (userData, onLogout) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Enhanced subscription check with CORRECT trial and payment logic
  const checkSubscription = useCallback(async (showNotifications = false) => {
    if (!userData?.buildingId || isNavigating) return;

    // Bypass trial/subscription expiration checks entirely for whitelisted emails
    if (isTrialBypassUser(userData?.email)) {
      setSubscriptionStatus({ isExpired: false, subscription: null });
      return;
    }

    try {

      const apiResponse = await getBuildingSubscription(userData.buildingId);

      if (!apiResponse) {
        setSubscriptionStatus({ isExpired: false, subscription: null });
        return;
      }

      // Handle wrapped response structure
      let subscriptionData;
      if (apiResponse.success && apiResponse.data) {
        subscriptionData = apiResponse.data;
      } else if (apiResponse.trialEndDate || apiResponse.nextPaymentDue) {
        subscriptionData = apiResponse;
      } else {
        return;
      }

      const currentDate = new Date();
      let isExpired = false;
      let expirationReason = '';
      let daysRemaining = 0;
      let warningType = 'info';

      // Check TrialEndDate
      let trialExpired = false;
      let trialDaysRemaining = 0;
      if (subscriptionData.trialEndDate) {
        const trialEndDate = new Date(subscriptionData.trialEndDate);

        // Check if trialEndDate is DateTime.MinValue
        const isMinValue = trialEndDate.getFullYear() <= 1970 || isNaN(trialEndDate.getTime());

        if (!isMinValue) {
          const trialTimeDiff = trialEndDate.getTime() - currentDate.getTime();
          trialDaysRemaining = Math.ceil(trialTimeDiff / (1000 * 60 * 60 * 24));
          trialExpired = currentDate > trialEndDate;
        }
      }

      // Check NextPaymentDue
      let paymentExpired = false;
      let paymentDaysRemaining = 0;
      if (subscriptionData.nextPaymentDue) {
        const nextPaymentDue = new Date(subscriptionData.nextPaymentDue);
        const paymentTimeDiff = nextPaymentDue.getTime() - currentDate.getTime();
        paymentDaysRemaining = Math.ceil(paymentTimeDiff / (1000 * 60 * 60 * 24));
        paymentExpired = currentDate > nextPaymentDue;
      }

      // CORRECT: Determine final status using CORRECT logic
      if (subscriptionData.trialEndDate && new Date(subscriptionData.trialEndDate).getFullYear() > 1970) {
        // Trial period exists and is not MinValue
        if (trialExpired && paymentExpired) {
          // ❌ BOTH trial AND payment expired - subscription is expired
          isExpired = true;
          expirationReason = 'Trial and payment periods expired';
          warningType = 'expired';
          daysRemaining = 0;
        } else if (!trialExpired) {
          // ✅ Trial is still active - subscription is active
          isExpired = false;
          daysRemaining = trialDaysRemaining;
          expirationReason = 'Trial period active';

          if (trialDaysRemaining <= 1) warningType = 'critical';
          else if (trialDaysRemaining <= 3) warningType = 'warning';
          else if (trialDaysRemaining <= 7) warningType = 'notice';
          else warningType = 'active';

        } else if (trialExpired && !paymentExpired) {
          // ✅ Trial expired but payment period still active - subscription is active
          isExpired = false;
          daysRemaining = paymentDaysRemaining;
          expirationReason = 'Payment period active (trial expired)';

          if (paymentDaysRemaining <= 1) warningType = 'critical';
          else if (paymentDaysRemaining <= 3) warningType = 'warning';
          else if (paymentDaysRemaining <= 7) warningType = 'notice';
          else warningType = 'active';
        }
      } else {
        // No trial or trial is MinValue - only check payment
        isExpired = paymentExpired;
        daysRemaining = paymentDaysRemaining;
        expirationReason = paymentExpired ? 'Payment period expired' : 'Payment period active';

        if (!paymentExpired) {
          if (paymentDaysRemaining <= 1) warningType = 'critical';
          else if (paymentDaysRemaining <= 3) warningType = 'warning';
          else if (paymentDaysRemaining <= 7) warningType = 'notice';
          else warningType = 'active';
        } else {
          warningType = 'expired';
        }
      }

      const status = {
        isExpired,
        subscription: subscriptionData,
        lastChecked: currentDate,
        daysRemaining,
        expirationReason,
        warningType,
        details: {
          trialExpired,
          paymentExpired,
          trialDaysRemaining,
          paymentDaysRemaining
        }
      };

      setSubscriptionStatus(status);
      setLastCheck(currentDate);


      // Handle different subscription states
      if (isExpired) {

        // Don't redirect if user is already in payment flow
        const currentPath = location.pathname;
        if (currentPath.includes('subscription-payment') ||
          currentPath.includes('payments') ||
          currentPath === '/' ||
          currentPath === '' ||
          isNavigating) {
          return;
        }

        console.log('🔥 SUBSCRIPTION EXPIRED - LOGGING OUT USER');
        console.log('Expired subscription details:', status.details);
        console.log('Building ID:', userData.buildingId);
        console.log('User will be logged out and redirected to landing page');
        
        // Store information about expired subscription for landing page
        localStorage.setItem('tempUser', JSON.stringify({
          selectedBuilding: {
            buildingId: userData.buildingId,
            buildingName: userData.buildingName || 'הבניין שלך'
          },
          fromLogin: true,
          subscriptionExpired: true
        }));
        
        // Force logout when subscription is expired
        setIsNavigating(true);
        onLogout(); // This will clear auth and redirect to landing page
        
        // Set expiration message in localStorage for landing page
        localStorage.setItem('subscriptionExpiredMessage', 'תקופת המנוי הסתיימה. יש לבחור תוכנית תשלום חדשה כדי להמשיך.');
        
        setTimeout(() => setIsNavigating(false), 2000);
        return;

      } else if (showNotifications) {
        // Show warnings based on warning type
        if (warningType === 'critical') {
          setWarningMessage(`⚠️ ${expirationReason} - נותרו ${daysRemaining} ימים! יש לבחור תוכנית תשלום כדי להמשיך לגשת למערכת.`);
          setShowWarning(true);
        } else if (warningType === 'warning') {
          setWarningMessage(`⚠️ ${expirationReason} - נותרו ${daysRemaining} ימים. מומלץ בחירת תוכנית תשלום בהקדם.`);
          setShowWarning(true);
        } else if (warningType === 'notice') {
          setWarningMessage(`ℹ️ ${expirationReason} - נותרו ${daysRemaining} ימים. כדאי להתכונן לבחירת תוכנית תשלום.`);
          setShowWarning(true);
        }

        // Auto-hide warning for non-critical warnings
        if (warningType !== 'critical' && showWarning) {
          setTimeout(() => {
            setShowWarning(false);
          }, 15000);
        }
      }

    } catch (error) {
      setSubscriptionStatus(prev => prev ? { ...prev, error: error.message } : null);
    }
  }, [userData?.buildingId, navigate, location.pathname, isNavigating]);

  // Manual function to go to payment
  const goToPayment = useCallback(() => {
    if (userData?.buildingId && !isNavigating) {
      setIsNavigating(true);
      navigate('/app/subscription-payment', {
        state: {
          buildingId: userData.buildingId,
          reason: 'manual_payment',
          returnTo: location.pathname,
          userProfile: userData
        }
      });
      setTimeout(() => setIsNavigating(false), 2000);
    }
  }, [userData, navigate, location.pathname, isNavigating]);

  // Dismiss warning
  const dismissWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  // Reset navigation flag when location changes successfully
  useEffect(() => {
    if (isNavigating) {
      const timer = setTimeout(() => {
        setIsNavigating(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isNavigating]);

  // Initial check and periodic monitoring
  useEffect(() => {
    if (userData?.buildingId && !isNavigating) {
      const initialTimeout = setTimeout(() => {
        checkSubscription(false);
      }, 500);

      const frequentInterval = setInterval(() => {
        if (!isNavigating) {
          checkSubscription(false);
        }
      }, 3 * 60 * 1000); // 3 minutes

      const notificationInterval = setInterval(() => {
        if (!isNavigating) {
          checkSubscription(true);
        }
      }, 15 * 60 * 1000); // 15 minutes

      return () => {
        clearTimeout(initialTimeout);
        clearInterval(frequentInterval);
        clearInterval(notificationInterval);
      };
    }
  }, [userData?.buildingId, checkSubscription, isNavigating]);

  // Check when user navigates
  useEffect(() => {
    if (userData?.buildingId && !location.pathname.includes('payment') && !isNavigating) {
      const timeout = setTimeout(() => {
        checkSubscription(false);
      }, 1500);

      return () => clearTimeout(timeout);
    }
  }, [location.pathname, checkSubscription, userData?.buildingId, isNavigating]);

  // Check when user becomes active again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userData?.buildingId && !isNavigating) {
        setTimeout(() => {
          if (!isNavigating) {
            checkSubscription(false);
          }
        }, 1000);
      }
    };

    const handleFocus = () => {
      if (userData?.buildingId && !isNavigating) {
        setTimeout(() => {
          if (!isNavigating) {
            checkSubscription(false);
          }
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userData?.buildingId, checkSubscription, isNavigating]);

  return {
    subscriptionStatus,
    lastCheck,
    showWarning,
    warningMessage,
    isNavigating,
    checkSubscription: () => !isNavigating && checkSubscription(true),
    goToPayment,
    dismissWarning
  };
};

// Notification Provider Component
const NotificationProvider = ({ children, userData, isAuthenticated }) => {
  const [globalNotifications, setGlobalNotifications] = useState({
    hasUnreadMessages: false,
    lastChecked: null
  });

  // Use the notification hook only for authenticated users
  const notificationData = useVaadsNotifications();

  useEffect(() => {
    if (isAuthenticated && userData?.userId && notificationData.hasUnread !== undefined) {
      setGlobalNotifications(prev => ({
        ...prev,
        hasUnreadMessages: notificationData.hasUnread,
        lastChecked: new Date()
      }));
    }
  }, [isAuthenticated, userData?.userId, notificationData.hasUnread]);

  // Function to manually refresh notifications
  const refreshNotifications = async () => {
    if (isAuthenticated && userData?.userId) {
      try {
        await notificationData.checkUnreadMessages();
      } catch (error) {
      }
    }
  };

  // Function to mark messages as read
  const markAsRead = async () => {
    if (isAuthenticated && userData?.userId) {
      try {
        await markMessagesAsRead(userData.userId);
        setGlobalNotifications(prev => ({
          ...prev,
          hasUnreadMessages: false,
          lastChecked: new Date()
        }));

        // Dispatch global event
        window.dispatchEvent(new CustomEvent('vaadsMessagesRead', {
          detail: { userId: userData.userId }
        }));
      } catch (error) {
      }
    }
  };

  const contextValue = {
    ...globalNotifications,
    refreshNotifications,
    markAsRead,
    isLoading: notificationData.loading
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    return {
      hasUnreadMessages: false,
      refreshNotifications: () => { },
      markAsRead: () => { },
      isLoading: false
    };
  }
  return context;
};

// Optional: Create a minimal admin layout component
const AdminLayout = ({ children, userData, onLogout }) => {
  return (
    <div className="admin-layout">
      {/* Optional: Add admin-specific header/navigation */}
      <header className="admin-header" style={{
        background: '#1e293b',
        color: 'white',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1>Admin Panel</h1>
        <div>
          <span>Welcome, {userData?.name || userData?.email}</span>
          <button
            onClick={onLogout}
            style={{
              marginLeft: '1rem',
              padding: '0.5rem 1rem',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </header>
      <main className="admin-content">
        {children}
      </main>
    </div>
  );
};

// Enhanced Layout Wrapper with Notifications and CORRECT Subscription Monitoring
const LayoutWrapper = ({ userData, onLogout, children }) => {
  const notifications = useNotifications();

  // Monitor subscription status with CORRECT monitoring
  const subscriptionMonitoring = useSubscriptionMonitoring(userData, onLogout);

  return (
    <>
      {/* Global Subscription Warning - only show if not navigating */}
      {!subscriptionMonitoring.isNavigating && (
        <SubscriptionWarning
          subscriptionStatus={subscriptionMonitoring.subscriptionStatus}
          showWarning={subscriptionMonitoring.showWarning}
          warningMessage={subscriptionMonitoring.warningMessage}
          onGoToPayment={subscriptionMonitoring.goToPayment}
          onDismiss={subscriptionMonitoring.dismissWarning}
        />
      )}

      <Layout
        userData={userData}
        onLogout={onLogout}
        notifications={notifications}
        subscriptionStatus={subscriptionMonitoring.subscriptionStatus}
        subscriptionMonitoring={subscriptionMonitoring} // Pass full monitoring object
      >
        {children}
      </Layout>
    </>
  );
};

// Main App component that handles routing
function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionExpiredMessage, setSubscriptionExpiredMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { networkError, hideNetworkError } = useError();

  useEffect(() => {

    // Setup auto-refresh for enhanced token manager
    const cleanupAutoRefresh = setupAutoRefresh();

    const checkAuthStatus = async () => {
      try {
        // 🔥 UPDATED: Use enhanced token manager methods instead of localStorage directly
        const token = enhancedTokenManager.getToken();
        const userData = enhancedTokenManager.getUserData();
        const rememberMe = enhancedTokenManager.isRememberMeEnabled();

        // Check if user was logged out due to expired subscription
        const subscriptionExpiredLogout = localStorage.getItem('subscriptionExpiredLogout');
        if (subscriptionExpiredLogout) {
          localStorage.setItem('subscriptionExpiredMessage', 'תקופת הניסיון הסתיימה. יש לבחור תוכנית תשלום כדי להמשיך.');
          localStorage.removeItem('subscriptionExpiredLogout');
        }


        if (token && !enhancedTokenManager.isTokenExpired(token) && userData) {

          // Store parsed user immediately to prevent race conditions
          setIsLoggedIn(true);
          setUserData(userData);

        } else {
          if (token && enhancedTokenManager.isTokenExpired(token)) {
            
            // Try to refresh token before clearing
            try {
              const newToken = await enhancedTokenManager.refreshToken();
              if (newToken) {
                const newUserData = enhancedTokenManager.getUserData();
                setIsLoggedIn(true);
                setUserData(newUserData);
              } else {
                enhancedTokenManager.clearAuth();
                setIsLoggedIn(false);
                setUserData(null);
              }
            } catch (refreshError) {
              enhancedTokenManager.clearAuth();
              setIsLoggedIn(false);
              setUserData(null);
            }
          } else {
            setIsLoggedIn(false);
            setUserData(null);
          }
        }
      } catch (error) {
        enhancedTokenManager.clearAuth();
        setIsLoggedIn(false);
        setUserData(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();

    // Cleanup auto-refresh on unmount
    return () => {
      if (cleanupAutoRefresh) {
        cleanupAutoRefresh();
      }
    };
  }, []);

  // 🔥 NEW: Listen for token expiration events
  useEffect(() => {
    const handleTokenExpired = (event) => {
      setIsLoggedIn(false);
      setUserData(null);
      navigate('/', { replace: true });
    };

    window.addEventListener('authTokenExpired', handleTokenExpired);
    return () => window.removeEventListener('authTokenExpired', handleTokenExpired);
  }, [navigate]);

  // Service Worker Registration
  useEffect(() => {
    const registerServiceWorker = () => {
      if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
          navigator.serviceWorker.register('/sw.js')
            .then((registration) => {

              // Check for updates
              registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;

                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed') {
                    if (navigator.serviceWorker.controller) {
                      // New version available - optionally show update notification
                    } else {
                      // Content is cached for offline use
                    }
                  }
                });
              });
            })
            .catch((registrationError) => {
            });
        });
      } else {
      }
    };

    // Register service worker when component mounts
    registerServiceWorker();
  }, []);

  // 🔥 UPDATED: Helper function to check if user is authenticated
  const isAuthenticated = () => {
    return enhancedTokenManager.validateSession() && isLoggedIn;
  };

  // Helper function to check if user is admin
  const isAdmin = () => {
    return userData?.email === 'motisegev1@gmail.com';
  };

  // Protected Route component
  const ProtectedRoute = ({ children }) => {
    if (isLoading) {
      return <div className="loading-screen">טוען...</div>;
    }
    return isAuthenticated() ? children : <Navigate to="/" replace />;
  };

  // Admin Route component - requires both authentication and admin privileges
  const AdminRoute = ({ children, useLayout = false }) => {
    if (isLoading) {
      return <div className="loading-screen">טוען...</div>;
    }

    if (!isAuthenticated()) {
      return <Navigate to="/" replace />;
    }

    if (!isAdmin()) {
      return <Navigate to="/app/dashboard" replace />;
    }

    if (useLayout) {
      return (
        <AdminLayout userData={userData} onLogout={handleLogout}>
          {children}
        </AdminLayout>
      );
    }

    return children;
  };

  // 🔥 UPDATED: Handle logout with proper navigation
  const handleLogout = () => {
    enhancedTokenManager.clearAuth();
    setIsLoggedIn(false);
    setUserData(null);
    navigate('/', { replace: true });
  };

  // 🔥 UPDATED: Handle successful login
  const handleLoginSuccess = (user, email) => {
    const userData = enhancedTokenManager.getUserData();
    setIsLoggedIn(true);
    setUserData(userData || {
      name: user,
      email: email
    });
    localStorage.removeItem('subscriptionExpiredMessage'); // Clear any expired message
    navigate('/app/dashboard', { replace: true });
  };

  if (isLoading) {
    return (
      <div className="loading-screen" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        טוען...
      </div>
    );
  }

  return (
    <div className="App">
      {/* Network Error Popup */}
      <NetworkErrorPopup
        isVisible={networkError.isVisible}
        message={networkError.message}
        onClose={hideNetworkError}
      />
      
      {/* Wrap the entire app with NotificationProvider for authenticated users */}
      <NotificationProvider
        userData={userData}
        isAuthenticated={isAuthenticated()}
      >
        <Routes>
          {/* Landing page - ALWAYS accessible */}
          <Route
            path="/"
            element={
              <LandingPage
                setIsLoggedIn={setIsLoggedIn}
                setUserData={setUserData}
                onLoginSuccess={handleLoginSuccess}
                isAuthenticated={isAuthenticated()}
                userData={userData}
                subscriptionExpiredMessage={subscriptionExpiredMessage}
              />
            }
          />

          {/* DIRECT ADMIN ROUTE */}
          <Route
            path="/admin"
            element={
              <AdminRoute useLayout={true}>
                <AdminDashboard userData={userData} />
              </AdminRoute>
            }
          />

          {/* Individual public routes redirect to landing page */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/register" element={<Navigate to="/" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* External routes - NO AUTHENTICATION REQUIRED */}
          <Route path="/external-dashboard" element={<ExtDashboard />} />
          <Route path="subscription-payment" element={<SubscriptionPayment />} />

          {/* External task route at root level - NO AUTHENTICATION REQUIRED */}
          <Route
            path="/external-task/:taskId/:encryptedBuildingId"
            element={<ExternalTaskPage />}
          />

          {/* Protected routes with Layout and Notifications - these require authentication */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <LayoutWrapper userData={userData} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard userData={userData} />} />
            <Route path="reports" element={<Reports />} />

            {/* Keep the old admin route for backward compatibility */}
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminDashboard userData={userData} />
                </AdminRoute>
              }
            />

            {/* New admin dashboard route */}
            <Route
              path="admin-dashboard"
              element={
                <AdminRoute>
                  <AdminDashboard userData={userData} />
                </AdminRoute>
              }
            />

            <Route path="users" element={<UsersManagement />} />
            <Route path="managers" element={<ManagersManagement />} />
            <Route path="management-company" element={<ManagementCompany />} />
            <Route path="messages" element={<Messages />} />
            <Route path="vaads-messages" element={<VaadsMessages />} />
            <Route path="tasks" element={<TaskManagement />} />
            <Route path="events" element={<Event />} />
            <Route path="payments" element={<Payment />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="electric-vehicle/*" element={<ElectricVehicle />} />
            <Route path="bank-status" element={<BankStatus />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="announcements" element={<Announcement />} />
            <Route path="issue" element={<Issue />} />
            <Route path="personal-information" element={<PersonalInformation />} />
            <Route path="settings" element={<Settings />} />
            <Route path="profile" element={<Profile userData={userData} />} />
            <Route path="add-building" element={<AddBuilding />} />
            <Route path="external" element={<ExtDashboard />} />
            <Route path="subscription-payment" element={<SubscriptionPayment />} />
          </Route>

          {/* Convenience redirects */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Navigate to="/app/dashboard" replace />
              </ProtectedRoute>
            }
          />

          {/* 404 route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </NotificationProvider>
    </div>
  );
}

// Wrapper component that provides Router context
function App() {
  return (
    <LanguageProvider>
      <DarkModeProvider>
        <ErrorProvider>
          <Router>
            <AppContent />
          </Router>
        </ErrorProvider>
      </DarkModeProvider>
    </LanguageProvider>
  );
}

export default App;